import { abtastyService } from '@/services';
import classNames from 'classnames';
import { debounce } from 'lodash';
import { useCallback, useRef, useEffect, useState } from 'react';
import { Link } from '@components/Link';

const SectionsNav = ({ elements }) => {
  const sectionsNavRef = useRef(null);
  const { sections } = useSections(sectionsNavRef, elements);

  const scrollToSectionHandler = useCallback(
    (section) => (e) => {
      e.preventDefault();
      const sectionId = e.currentTarget.getAttribute('href').replace('#', '');
      const sectionEl = document.getElementById(sectionId);
      if (sectionEl) {
        const sectionElOffset = sectionEl.getBoundingClientRect().top;
        window.scrollTo({
          top: sectionElOffset + window.pageYOffset - 72, // menuHeight
          behavior: 'smooth',
        });
      }
      abtastyService.trackClick(section.trackingLabel);
    },
    [sectionsNavRef]
  );

  if (!sections || sections.length === 0) return null;

  return (
    <>
      <div id='BuyNow'></div>
      <div
        id='SectionsNav'
        className='tw:sticky tw:top-[55px] tw:z-50 tw:flex tw:w-screen tw:bg-white tw:p-3 tw:shadow-[0px_3px_6px_#00000019] tw:md:items-center tw:md:justify-center tw:md:p-5'>
        <ul
          ref={sectionsNavRef}
          className='scrollbar-hide tw:mb-0 tw:flex tw:max-w-full tw:list-none tw:gap-6 tw:overflow-x-auto tw:px-2 tw:md:items-center tw:md:justify-center tw:md:gap-16'>
          {sections.map((s) => (
            <li key={s.id} className='tw:flex tw:whitespace-nowrap'>
              <Link
                href={`#${s.id}`}
                onClick={scrollToSectionHandler(s)}
                className='tw:inline-block tw:text-sm tw:font-medium tw:text-body tw:transition tw:duration-150 tw:ease-in-out tw:hover:text-body tw:hover:!no-underline tw:md:hover:opacity-60 tw:lg:text-base'>
                <div className='tw:flex tw:items-center tw:gap-2'>
                  <img src={s.icon} alt={s.title} className='tw:h-[18px] tw:max-w-[18px]' />
                  <span
                    className={classNames('tw:inline-block', {
                      // 'tw:font-bold': activeSection === s.id,
                    })}>
                    {s.title}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const useSections = (sectionsNavRef, elements) => {
  const [sections, setSections] = useState<any[]>([]);
  const [activeSection, setActiveSection] = useState<any>(null);

  useEffect(() => {
    setSections(getSections(elements));
  }, [elements]);

  useEffect(() => {
    let sectionPositions = [];

    const handleScroll = debounce(() => {
      requestAnimationFrame(() => {
        const scrollPosition = window.scrollY;

        const section = sectionPositions.find((s) => {
          if (!s) return false;
          return scrollPosition >= s.top;
        });

        if (section) {
          setActiveSection(section.id);

          const activeSectionEl = sectionsNavRef.current.querySelector(`a[href="#${section.id}"]`);
          if (activeSectionEl) {
            const activeSectionElOffset = activeSectionEl.getBoundingClientRect().left;
            const sectionsNavEl = sectionsNavRef.current;
            sectionsNavEl.scrollTo({
              left: activeSectionElOffset + sectionsNavEl.scrollLeft - sectionsNavEl.clientWidth / 2,
              behavior: 'smooth',
            });
          }
        } else {
          setActiveSection(null);
        }
      });
    }, 30);

    setTimeout(() => {
      sectionPositions = sections
        .map((s) => {
          const el = document.getElementById(s.id);
          if (!el) return null;

          const top = el.getBoundingClientRect().top + window.pageYOffset - 72; // menuHeight

          return { id: s.id, top };
        })
        .filter((s) => !!s && s.id !== 'BuyNow')
        .reverse();
    }, 1500);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  return { sections, activeSection };
};

const getSections = (elements: any) => {
  const sections = [];

  if (elements.length > 0) {
    for (const element of elements) {
      if (element.elementType === 'slides' && element.attributes.role === 'whyWeLoveIt') {
        sections.push({
          icon: require('../images/icons/icon-why-we-love-it.svg'),
          title: 'Why We Love It',
          id: 'WhyWeLoveIt',
          trackingLabel: 'Sub Nav: Why We Love It',
        });
      } else if (element.elementType === 'slides' && element.attributes.role === 'features') {
        sections.push({
          icon: require('../images/icons/icon-tech-features.svg'),
          title: 'Tech & Features',
          id: 'TechAndFeatures',
        });
      } else if (element.elementType === 'capacity') {
        sections.push({
          icon: require('../images/icons/icon-capacity.svg'),
          title: 'Size & Capacity',
          id: 'Capacity',
        });
      }
    }

    if (sections.length > 0)
      sections.push({
        icon: require('../images/icons/icon-buy-now.svg'),
        title: 'Buy Now',
        id: 'BuyNow',
        trackingLabel: 'Sub Nav: Buy Now',
      });

    return sections;
  }

  if (sections.length > 0)
    sections.push({
      icon: require('../images/icons/icon-buy-now.svg'),
      title: 'Buy Now',
      id: 'BuyNow',
      trackingLabel: 'Sub Nav: Buy Now',
    });

  return sections;
};

export default SectionsNav;
